import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { X, ChevronDown, Filter as FilterIcon } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * FilterBar — horizontal multi-select filter bar.
 *
 * Replaces the old left sidebar. Sits above the recipe grid on both desktop
 * and mobile. Each filter category opens a popover with checkboxes (multi-
 * select). Active selections render as removable chips below the bar.
 */
export const FilterBar = ({ filters, setFilters }) => {
  const { t, language } = useAuth();
  const [filterOptions, setFilterOptions] = useState(null);

  useEffect(() => {
    fetch(`${API_URL}/api/filters`)
      .then((r) => r.json())
      .then(setFilterOptions)
      .catch(() => {});
  }, []);

  const getLabel = (item) => (language === 'cz' ? item.label_cz : item.label_sk);

  const toggleFilter = (type, value) => {
    setFilters((prev) => {
      const current = prev[type] ? prev[type].split(',') : [];
      const next = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
      return { ...prev, [type]: next.length ? next.join(',') : '' };
    });
  };

  const isChecked = (type, value) => {
    const cur = filters[type] ? filters[type].split(',') : [];
    return cur.includes(value);
  };

  const countSelected = (type) => {
    return filters[type] ? filters[type].split(',').filter(Boolean).length : 0;
  };

  const clearAll = () => setFilters({});

  const removeOne = (type, value) => {
    const cur = filters[type] ? filters[type].split(',') : [];
    const next = cur.filter((v) => v !== value);
    setFilters((prev) => ({ ...prev, [type]: next.length ? next.join(',') : '' }));
  };

  const totalActive = ['meal_type', 'season', 'category', 'difficulty']
    .reduce((acc, k) => acc + countSelected(k), 0);

  const groups = [
    { key: 'meal_type', label: t('mealType'), items: filterOptions?.meal_types || [] },
    { key: 'season', label: t('season'), items: filterOptions?.seasons || [] },
    { key: 'category', label: t('category'), items: filterOptions?.categories || [] },
    { key: 'difficulty', label: t('difficulty'), items: filterOptions?.difficulties || [] },
  ];

  const FilterButton = ({ group }) => {
    const selected = countSelected(group.key);
    return (
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant={selected > 0 ? 'default' : 'outline'}
            size="sm"
            className={cn(
              'rounded-full h-9 px-4 text-sm font-medium gap-2 whitespace-nowrap',
              selected > 0
                ? 'bg-primary text-white border-primary hover:bg-primary/90'
                : 'border-border bg-white text-text-heading hover:border-primary/50'
            )}
            data-testid={`filter-trigger-${group.key}`}
          >
            <span>{group.label}</span>
            {selected > 0 && (
              <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-white/25 text-xs font-semibold">
                {selected}
              </span>
            )}
            <ChevronDown className="w-3.5 h-3.5 opacity-70" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="w-64 p-3 max-h-[60vh] overflow-y-auto"
          data-testid={`filter-panel-${group.key}`}
        >
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2 px-1">
            {group.label}
          </p>
          <div className="space-y-1">
            {group.items.map((item) => (
              <label
                key={item.value}
                className="flex items-center gap-2.5 px-2 py-1.5 rounded-lg hover:bg-muted cursor-pointer"
              >
                <Checkbox
                  checked={isChecked(group.key, item.value)}
                  onCheckedChange={() => toggleFilter(group.key, item.value)}
                  data-testid={`filter-${group.key}-${item.value}`}
                />
                <span className="text-sm text-text-heading flex-1">{getLabel(item)}</span>
              </label>
            ))}
          </div>
        </PopoverContent>
      </Popover>
    );
  };

  // Render the active-filter chips below the bar
  const chips = [];
  groups.forEach((group) => {
    const cur = filters[group.key] ? filters[group.key].split(',') : [];
    cur.forEach((val) => {
      const item = group.items.find((i) => i.value === val);
      if (item) chips.push({ groupKey: group.key, value: val, label: getLabel(item), groupLabel: group.label });
    });
  });

  return (
    <div className="mb-6" data-testid="filter-bar">
      {/* Filter triggers (horizontal scroll on mobile) */}
      <div className="flex items-center gap-2 overflow-x-auto -mx-1 px-1 pb-1 scrollbar-thin">
        <FilterIcon className="w-4 h-4 text-muted-foreground shrink-0 ml-1" />
        {groups.map((g) => (
          <FilterButton key={g.key} group={g} />
        ))}
        {totalActive > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={clearAll}
            className="rounded-full h-9 px-3 text-xs text-muted-foreground hover:text-destructive whitespace-nowrap"
            data-testid="clear-all-filters"
          >
            <X className="w-3.5 h-3.5 mr-1" />
            {language === 'sk' ? 'Vymazať všetky' : 'Vymazat vše'}
          </Button>
        )}
      </div>

      {/* Active filter chips */}
      {chips.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-3" data-testid="active-filter-chips">
          {chips.map((c) => (
            <button
              key={`${c.groupKey}-${c.value}`}
              type="button"
              onClick={() => removeOne(c.groupKey, c.value)}
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/20 transition-colors"
              data-testid={`active-chip-${c.groupKey}-${c.value}`}
            >
              <span className="text-muted-foreground">{c.groupLabel}:</span>
              <span>{c.label}</span>
              <X className="w-3 h-3" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
